<template>
  <div :class="$style.container">
    <SettingsPagePanel>
      <CalendarAuthView />
    </SettingsPagePanel>

    <SettingsPagePanel>
      <SettingsPagePanelHeader :showSubtitle="true">
        <template #title>日程調整カレンダー</template>
        <template #right v-if="canEditCalendarSetting">
          <RouterLink :to="{ name: 'calendarSettingCreate' }">
            <FlatButton>
              <template #icon>
                <IconPlus />
              </template>
              新規作成
            </FlatButton>
          </RouterLink>
        </template>
      </SettingsPagePanelHeader>

      <template v-if="loading">
        <p>Now loading...</p>
      </template>
      <div
        v-else-if="calendarSummaries && linkedStaffGUIDs"
        :class="$style.listContainer"
      >
        <DefaultCalendarSettings
          :availableCalendars="availableCalendars"
          :canEdit="canEditCalendarSetting"
        />
        <WarningCalendarSummaryView
          :calendarSummaries="calendarSummaries"
          :linkedStaffGUIDs="linkedStaffGUIDs"
        />
        <CalendarSummaryView
          :calendarSummaries="calendarSummaries"
          :linkedStaffGUIDs="linkedStaffGUIDs"
        />
      </div>
      <template v-else>
        <p>日程調整カレンダーがありません</p>
      </template>
    </SettingsPagePanel>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPlus from '@/components/icons/IconPlus.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'

import CalendarAuthView from './CalendarAuthView.vue'
import WarningCalendarSummaryView from './WarningCalendarSummaryView/WarningCalendarSummaryView.vue'
import CalendarSummaryView from './CalendarSummaryView/CalendarSummaryView.vue'
import DefaultCalendarSettings from './DefaultCalendarSettings.vue'

import {
  useFetchCalendarSummariesQuery,
  useCalendarSettingsPageQuery,
  type FetchCalendarSummariesQuery,
} from './SettingsPageCalendarView.generated.ts'
import { CalendarStatus } from '@/types/schema.generated.ts'
import { useCalendar } from '@/hooks/useCalendar.ts'

const { result: calendarSettingsPageQueryResult } =
  useCalendarSettingsPageQuery()
const canEditCalendarSetting = computed(
  () =>
    calendarSettingsPageQueryResult.value?.uiControl
      .editOkanekoCalendarSetting ?? false
)

// カレンダー一覧の取得
const { result: calendarSummariesQueryResult, loading } =
  useFetchCalendarSummariesQuery({
    fetchPolicy: 'no-cache',
  })

const calendarSummaries = computed<
  | FetchCalendarSummariesQuery['currentEntrance']['calendarSummaries']
  | undefined
>(() => calendarSummariesQueryResult.value?.currentEntrance.calendarSummaries)

const availableCalendars = computed(() => {
  if (!calendarSummaries.value) {
    return []
  }

  return calendarSummaries.value
    .filter(c => c.status === CalendarStatus.Active)
    .map(c => ({
      guid: c.guid,
      publicTitle: c.publicTitle,
      privateTitle: c.privateTitle ? c.privateTitle : undefined,
    }))
})

// アカウント連携済みスタッフ
const { linkedStaffs } = useCalendar()
const linkedStaffGUIDs = computed(() => {
  if (!linkedStaffs.value) {
    return undefined
  }
  return linkedStaffs.value.map(s => s.guid)
})
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.listContainer {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
